import { useEffect, useLayoutEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ChatBubble } from "./ChatBubble";
import type { ChatViewMessage, ChatViewPagination } from "./types";

interface ChatMessageListProps {
  /** Messages in chronological order (oldest first). Accumulated across pages. */
  messages: ChatViewMessage[];
  pagination: ChatViewPagination;
  /** How many messages have been loaded across all pages so far. */
  totalLoaded: number;
  isLoading: boolean;
  /** Whether there are still older pages to fetch. */
  hasOlderMessages: boolean;
  onLoadOlder: () => void;
  /**
   * Opaque key that the parent bumps whenever the view should fully reset
   * (e.g. new filters applied, different conversation selected).
   * Changing this resets internal scroll / accumulation tracking.
   */
  resetKey?: string | number;
}

export function ChatMessageList({
  messages,
  pagination,
  totalLoaded,
  isLoading,
  hasOlderMessages,
  onLoadOlder,
  resetKey,
}: ChatMessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevMessageCount = useRef(0);
  const hasScrolledToBottom = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const prevResetKey = useRef(resetKey);

  // Reset all internal tracking when resetKey changes.
  useEffect(() => {
    if (prevResetKey.current !== resetKey) {
      prevResetKey.current = resetKey;
      hasScrolledToBottom.current = false;
      prevMessageCount.current = 0;
      prevScrollHeightRef.current = 0;
    }
  }, [resetKey]);

  // On first load (or filter change → new page 1 data), scroll to bottom.
  useEffect(() => {
    if (pagination.currentPage === 1 && !isLoading && messages.length > 0 && !hasScrolledToBottom.current) {
      bottomRef.current?.scrollIntoView({ behavior: "instant" as ScrollBehavior });
      hasScrolledToBottom.current = true;
    }
  }, [pagination.currentPage, isLoading, messages.length]);

  // Reset scroll flag when filters change (page goes back to 1 with no messages yet)
  useEffect(() => {
    if (pagination.currentPage === 1 && messages.length === 0) {
      hasScrolledToBottom.current = false;
    }
  }, [pagination.currentPage, messages.length]);

  // When older messages are prepended, preserve the scroll position so the user
  // stays looking at the same messages they were reading before.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const newCount = messages.length;
    const oldCount = prevMessageCount.current;

    // Only adjust when messages were prepended (count grew and we're past page 1)
    if (newCount > oldCount && oldCount > 0 && pagination.currentPage > 1) {
      // The new messages were added to the top of the DOM.
      // scrollHeight grew by the height of the new messages.
      // We need to offset scrollTop by that difference.
      if (prevScrollHeightRef.current > 0) {
        const delta = container.scrollHeight - prevScrollHeightRef.current;
        container.scrollTop = container.scrollTop + delta;
      }
    }

    prevMessageCount.current = newCount;
  }, [messages.length, pagination.currentPage]);

  // Capture scrollHeight synchronously before triggering the load so the
  // value is immune to intermediate re-renders (e.g. isLoading toggling).
  const handleLoadOlder = () => {
    if (containerRef.current) {
      prevScrollHeightRef.current = containerRef.current.scrollHeight;
    }
    onLoadOlder();
  };

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-3 overflow-y-auto flex-1 p-4"
    >
      {/* Load older button */}
      {hasOlderMessages && (
        <div className="flex justify-center py-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadOlder}
            disabled={isLoading}
            className="gap-2"
          >
            {isLoading ? "Loading..." : "Load older messages"}
          </Button>
        </div>
      )}

      {/* Loading state for initial load only */}
      {isLoading && messages.length === 0 && !hasOlderMessages && (
        <div className="flex items-center justify-center flex-1">
          Loading...
        </div>
      )}

      {/* Empty state */}
      {!isLoading && messages.length === 0 && (
        <div className="flex items-center justify-center flex-1 text-muted-foreground">
          No messages found for the current filters.
        </div>
      )}

      {/* Messages */}
      {messages.map((msg) => (
        <ChatBubble key={msg.id} message={msg} />
      ))}

      {/* Pagination info */}
      {messages.length > 0 && (
        <div className="text-center text-xs text-muted-foreground py-1">
          Showing {totalLoaded} of {pagination.totalItems} messages
          {pagination.totalPages > 1 && ` · Loaded through page ${pagination.currentPage} of ${pagination.totalPages}`}
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
